'use client'

import { createBrowserClient } from '@vagahub/db/browser'
import type { BrowserAuthUser } from '@vagahub/db/browser'
import { Download, LogOut, Menu, X, Zap } from 'lucide-react'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { TopMarquee } from '../layout/TopMarquee'
import { ThemeToggle } from '../theme-toggle'
import { Button } from '../ui/button'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function Header() {
  const [user, setUser] = useState<BrowserAuthUser | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    const supabase = createBrowserClient()
    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user)
    })

    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null)
    })

    const handleInstallPrompt = (e: Event) => {
      const customEvent = e as CustomEvent<BeforeInstallPromptEvent>
      setDeferredPrompt(customEvent.detail)
    }

    window.addEventListener('pwa-beforeinstallprompt', handleInstallPrompt)

    return () => {
      listener.subscription.unsubscribe()
      window.removeEventListener('pwa-beforeinstallprompt', handleInstallPrompt)
    }
  }, [])

  const handleInstallApp = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    console.log(`PWA escolha do usuário no Header: ${outcome}`)
    setDeferredPrompt(null)
  }

  const handleLogout = async () => {
    const supabase = createBrowserClient()
    await supabase.auth.signOut()
    // Achado da auditoria 2026-09-10: snapshot de busca (search-cache.ts)
    // sobrevivia ao logout, em session/localStorage — próximo usuário no
    // mesmo aparelho via back-button e caso a sessão do último usuário.
    const { clearSnapshot } = await import('@/utils/search-cache')
    clearSnapshot()
    window.location.reload()
  }

  return (
    <header className="sticky top-0 z-50 border-b border-blue-100/50 dark:border-white/5 backdrop-blur-md dark:bg-slate-950/90 shadow-sm overflow-hidden">
      {/* Background Light Gradient Layer matching Home */}
      <div className="absolute inset-0 bg-warm-gradient opacity-[0.85] -z-10 dark:hidden" />
      <TopMarquee />
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link
          href="/auth/login"
          className="flex items-center gap-2.5 group"
          aria-label="AcheiVagas — entrar"
        >
          <div className="relative">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow duration-300">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-none">
              AcheiVagas
            </h1>
            <span className="text-[10px] text-muted-foreground tracking-wider uppercase">
              .com.br
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link href="/buscar?categoria=emprego">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Empregos
            </Button>
          </Link>
          <Link href="/buscar?categoria=hospedagem">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Hospedagem
            </Button>
          </Link>
          <Link href="/buscar?categoria=estacionamento">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Estacionamento
            </Button>
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {deferredPrompt && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleInstallApp}
              title="Instalar AcheiVagas"
              className="text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <Download className="h-5 w-5" />
            </Button>
          )}
          <ThemeToggle />

          {user ? (
            <>
              <Link href="/buscar">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-md shadow-blue-500/20 hover:shadow-blue-500/30 transition-all font-semibold flex items-center gap-1.5"
                >
                  Buscar Vagas ↗
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-gray-300/80 bg-white/60 hover:bg-white text-gray-700 hover:text-red-600 dark:hover:bg-red-950/20 dark:hover:text-red-400 backdrop-blur-sm font-medium flex items-center gap-1.5"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Sair</span>
              </Button>
            </>
          ) : (
            <>
              <Link href="/auth/login" className="hidden sm:block">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-300/80 bg-white/60 hover:bg-white text-gray-700 hover:text-gray-900 backdrop-blur-sm font-medium"
                >
                  Entrar
                </Button>
              </Link>
              <Link href="/auth/register">
                <Button
                  size="sm"
                  className="h-11 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-md shadow-blue-500/20 hover:shadow-blue-500/30 transition-all"
                >
                  <Zap className="h-3.5 w-3.5 mr-1" />
                  Cadastrar
                </Button>
              </Link>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <div className="container mx-auto px-4 py-3 space-y-1">
            {[
              { href: '/buscar?categoria=emprego', label: 'Empregos' },
              { href: '/buscar?categoria=hospedagem', label: 'Hospedagem' },
              { href: '/buscar?categoria=estacionamento', label: 'Estacionamento' },
            ].map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                className="block"
              >
                <span className="flex w-full items-center px-3 h-11 rounded-lg text-sm font-medium text-gray-700 dark:text-zinc-200 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors">
                  {label}
                </span>
              </Link>
            ))}
            {user ? (
              <>
                <Link href="/buscar" onClick={() => setMobileMenuOpen(false)} className="block">
                  <span className="flex w-full items-center px-3 h-11 rounded-lg text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors">
                    Buscar Vagas
                  </span>
                </Link>
                <div className="pt-1 pb-1">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false)
                      handleLogout()
                    }}
                    className="flex w-full items-center justify-center gap-1.5 px-3 h-11 rounded-lg text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:text-red-400 transition-all w-full"
                  >
                    <LogOut className="h-4 w-4" />
                    Sair
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  href="/auth/login"
                  className="block sm:hidden"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="flex w-full items-center px-3 h-11 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors">
                    Entrar
                  </span>
                </Link>
                <div className="pt-1 pb-1">
                  <Link
                    href="/auth/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block"
                  >
                    <span className="flex w-full items-center justify-center px-3 h-11 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 transition-all">
                      Cadastrar grátis
                    </span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
